import { createAsyncThunk } from '@reduxjs/toolkit';

import { IQuestion, IQuestionDetailAnswer, TQuestionDifficulty } from '.';
import { AdminApiPaths, APIDataProvider, TApiResponse } from '../../api';

export interface IAdminGetQuestionList {
  limit: number;
  offset: number;
  subTopicIds: Array<string>;
}

export interface IAdminGetQuestions {
  limit: number;
  offset: number;
  questionId?: string;
}

export interface IAdminCreateQuestion {
  subTopics: Array<{ subTopic: string; paper: string }>;
  difficulty: TQuestionDifficulty;
  source?: string;
  marks?: number;
  tags?: string[];
  details: string;
  markingSchema: string;
  answers: IQuestionDetailAnswer[];
  theoryQuestion?: { question: string };
}
export interface IAdminUpdateQuestion {
  id: string;
  subTopics: Array<{ subTopic: string; paper: string }>;
  difficulty: TQuestionDifficulty;
  source?: string;
  marks?: number;
  tags?: string[];
  details: string;
  markingSchema: string;
  answers: IQuestionDetailAnswer[];
  theoryQuestion?: { question: string };
}
export interface IAdminApproveQuestion {
  id: string;
  isApproved: boolean;
}

const apiDataProvider = new APIDataProvider();

const adminGetQuestionListModule = async (data: IAdminGetQuestionList) => {
  const path = `${AdminApiPaths.questions}/subtopics?limit=${
    data.limit
  }&offset=${data.offset}&subTopicIds=${data.subTopicIds.join(',')}`;

  const response = await apiDataProvider.get<
    TApiResponse<{
      limit: number;
      offset: number;
      totalDocs: number;
      docs: Array<IQuestion>;
    }>
  >(path);

  return response.data;
};

const adminGetQuestionsModule = async (data: IAdminGetQuestions) => {
  let path = `${AdminApiPaths.questions}?limit=${data.limit}&offset=${data.offset}`;

  if (data.questionId) {
    path += `&questionId=${data.questionId}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<{
      limit: number;
      offset: number;
      totalDocs: number;
      docs: Array<IQuestion>;
    }>
  >(path);

  return response.data;
};

export const adminGetQuestionById = createAsyncThunk(
  'admin/question/id',
  async (id: string) => {
    const response = await apiDataProvider.get<TApiResponse<IQuestion>>(
      `${AdminApiPaths.questions}/${id}`
    );

    return response.data;
  }
);

export const adminCreateNewQuestion = createAsyncThunk(
  'admin/question/create',
  async (data: IAdminCreateQuestion) => {
    const response = await apiDataProvider.post<TApiResponse<IQuestion>>(
      AdminApiPaths.questions,
      data
    );

    return response.data;
  }
);

export const adminCreateNewQuestions = createAsyncThunk(
  'admin/questions/create-many',
  async (data: { questions: IAdminCreateQuestion[] }) => {
    const response = await apiDataProvider.post<TApiResponse<IQuestion[]>>(
      `${AdminApiPaths.questions}/create-many`,
      data
    );

    return response.data;
  }
);

export const adminUpdateQuestion = createAsyncThunk(
  'admin/question/update',
  async (data: IAdminUpdateQuestion) => {
    const response = await apiDataProvider.put<TApiResponse<IQuestion>>(
      `${AdminApiPaths.questions}/${data.id}`,
      data
    );

    return response.data;
  }
);

export const adminApproveQuestion = createAsyncThunk(
  'admin/question/approve',
  async (data: IAdminApproveQuestion) => {
    const response = await apiDataProvider.put<TApiResponse<IQuestion>>(
      `${AdminApiPaths.questions}/approve/${data.id}`,
      data
    );

    return response.data;
  }
);

export const adminDeleteQuestion = createAsyncThunk(
  'admin/question/delete',
  async (data: { id: string }) => {
    const response = await apiDataProvider.put<TApiResponse<IQuestion>>(
      `${AdminApiPaths.questions}/delete/${data.id}`,
      data
    );

    return response.data;
  }
);

export const adminGetQuestionList = createAsyncThunk(
  'admin/question/list',
  adminGetQuestionListModule
);

export const adminLoadMoreQuestionList = createAsyncThunk(
  'admin/question/loadMore',
  adminGetQuestionListModule
);

export const adminGetQuestions = createAsyncThunk(
  'admin/questions/all',
  adminGetQuestionsModule
);

export const adminLoadMoreQuestions = createAsyncThunk(
  'admin/questions/all/loadMore',
  adminGetQuestionsModule
);
